import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, of, timeout } from 'rxjs';
import { GlobalService } from 'src/app/_services/global.service';
import { PlanDataService } from 'src/app/_services/plan-data.service';
import { SignupService } from 'src/app/_services/signup.service';

/** Standalone "Hardware & Accessories" shop (LincSell Signup.dc.html prototype's `isHardware`
 *  screen) — lets an already-subscribed merchant browse/buy hardware from the sidebar at any time,
 *  rather than only during the initial plan-purchase checkout. Hands the selection off to
 *  PaymentPageComponent (/check-out) the same way billing-and-plans.component.ts's "Buy more"
 *  actions do (checkout-usage-intent), with allowHardware/presetHardware so that page's
 *  hardwareSupported gate — normally forced off for a usage-only cart — opens for this trip only. */
@Component({
    selector: 'app-hardware-shop',
    templateUrl: './hardware-shop.component.html',
    styleUrls: ['./hardware-shop.component.scss'],
})
export class HardwareShopComponent implements OnInit {
    _global = GlobalService;

    isLoading = true;
    hardwaresList: any[] = [];

    /** Shop grid, grouped the same way the API groups it — one section per
     *  GetHardwarePackagesv2 category (packageTitle), in API order. Replaces the old fixed
     *  mainDevices/additionalDevices/peripherals index assumption so a new category or a
     *  reordering on the backend shows up here without a code change. */
    hardwareCategories: { title: string; items: any[] }[] = [];

    /** Hardware catalog id -> {title, image}, built alongside hardwaresList in loadHardwareList() —
     *  purchasedHardwareList needs it to resolve subscriptionPlanPkg's bare packageId into a
     *  display name and thumbnail. */
    private hardwareCatalog = new Map<string, { title: string; image: string }>();

    constructor(
        private _signup: SignupService,
        private _planData: PlanDataService,
        private _router: Router,
    ) { }

    ngOnInit(): void {
        this.loadHardwareList();
    }

    /** Already-purchased hardware (POS terminals, card readers, accessories). Reads
     *  businessSetup[0].subscription.subscriptionPlanPkg — the same purchased-line-items array
     *  billing-and-plans-calculation.ts's packageLineById()/matchingPackageLine() already read for
     *  register/location/addon pricing — since GetBusinessInvoice's own currentSubscription/
     *  hardwareList/subscribedPackagesV2 fields came back null against a real account in testing.
     *  Hardware lines are identified by packageType === 1 (confirmed against real purchase data —
     *  registers/locations are packageType 0) OR by packageId matching the hardware catalog (covers
     *  older purchases recorded before packageType was set), always excluding the register/location
     *  packageIds ('1'/'2') to be safe either way. See billing-and-plans.component.ts's
     *  purchasedHardwareList for the identical, verified-working implementation. */
    get purchasedHardwareList(): any[] {
        const setup = this._global.userInfo?.businessSetup?.[0];
        const lines: any[] = setup?.subscription?.subscriptionPlanPkg ?? setup?.Subscription?.SubscriptionPlanPkg ?? [];
        return lines
            .filter((line: any) => {
                const packageId = String(line?.packageId ?? line?.PackageId ?? '');
                if (packageId === '1' || packageId === '2') return false;
                const packageType = this.toNumber(line?.packageType ?? line?.PackageType);
                return packageType === 1 || this.hardwareCatalog.has(packageId);
            })
            .map((line: any) => {
                const packageId = String(line?.packageId ?? line?.PackageId ?? '');
                const catalogEntry = this.hardwareCatalog.get(packageId);
                return {
                    name: catalogEntry?.title || packageId,
                    image: catalogEntry?.image || '',
                    qty: this.toNumber(line?.qty ?? line?.Qty),
                    price: this.toNumber(line?.totalPrice ?? line?.TotalPrice ?? line?.subTotalCost ?? line?.SubTotalCost),
                    purchasedDate: line?.createdDate ?? line?.CreatedDate ?? null,
                    packageId: line?.id ?? line?.Id ?? packageId,
                };
            });
    }

    get purchasedHardwareTotalQty(): number {
        return this.purchasedHardwareList.reduce((sum, h) => sum + h.qty, 0);
    }

    get purchasedHardwareTotalPrice(): number {
        return this.purchasedHardwareList.reduce((sum, h) => sum + h.price, 0);
    }

    trackHardwareByName(_index: number, hardware: any): any {
        return hardware.packageId ?? hardware.name;
    }

    private loadHardwareList(): void {
        this.isLoading = true;
        this._signup.getHardwarePlanListv2().pipe(
            timeout(10000),
            catchError(() => of({ success: false, data: [] })),
            finalize(() => this.isLoading = false),
        ).subscribe((res: any) => {
            if (!res?.success || !Array.isArray(res.data)) {
                this.hardwaresList = [];
                this.hardwareCategories = [];
                return;
            }
            const groups = res.data;

            this.hardwareCategories = groups
                .map((group: any) => ({
                    title: group?.packageTitle || group?.packageTypeString || '',
                    items: (Array.isArray(group?.items) ? group.items : [])
                        .filter((item: any) => item?.isActive)
                        .map((item: any) => ({ ...item, quantity: 0 })),
                }))
                .filter((group: { title: string; items: any[] }) => group.items.length);

            this.hardwaresList = this.hardwareCategories.flatMap((group) => group.items);

            // Full catalog (not just the curated shop list above) so purchasedHardwareList can
            // resolve any hardware packageId to a name/image, including items no longer shown for sale.
            const catalog = new Map<string, { title: string; image: string }>();
            for (const group of groups) {
                for (const item of (Array.isArray(group?.items) ? group.items : [])) {
                    if (item?.id != null) catalog.set(String(item.id), { title: item.title, image: item.image });
                }
            }
            this.hardwareCatalog = catalog;
        });
    }

    private toNumber(value: any): number {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    /** Item currently shown in the info popup, or null when it's closed. */
    infoItem: any = null;

    get infoDialogVisible(): boolean {
        return this.infoItem !== null;
    }

    set infoDialogVisible(value: boolean) {
        if (!value) this.infoItem = null;
    }

    /** packageTypeString ("PaymentDevices", "Peripherals", ...) -> reader-friendly subtitle. */
    private readonly packageTypeSubtitles: Record<string, string> = {
        PaymentDevices: 'Payment device',
        Peripherals: 'Hardware accessory',
    };

    getHardwareInfo(item: any): { subtitle: string; description: string; features: string[] } {
        const subtitle = item?.note || this.packageTypeSubtitles[item?.packageTypeString] || 'Compatible LincSell hardware';
        const description = this.stripHtml(item?.rawData) || 'Compatible LincSell hardware for in-store selling.';
        const features = String(item?.featureHeading ?? '')
            .split(',')
            .map((f: string) => f.trim())
            .filter(Boolean);
        return { subtitle, description, features };
    }

    private stripHtml(value: any): string {
        return String(value ?? '').replace(/<[^>]*>/g, '').trim();
    }

    openInfo(item: any): void {
        this.infoItem = item;
    }

    closeInfo(): void {
        this.infoItem = null;
    }

    addToOrderFromInfo(): void {
        if (!this.infoItem) return;
        this.increment(this.infoItem);
        this.closeInfo();
    }

    increment(item: any): void {
        item.quantity = (item.quantity || 0) + 1;
    }

    decrement(item: any): void {
        if (item.quantity > 0) item.quantity--;
    }

    get selectedItems(): any[] {
        return this.hardwaresList.filter((h) => h.quantity > 0);
    }

    get selectedCount(): number {
        return this.selectedItems.length;
    }

    get selectedTotal(): number {
        return this.selectedItems.reduce((sum, item) => sum + (this.toNumber(item.price) * item.quantity), 0);
    }

    /** Same cosmosId match PaymentPageComponent.resolvePlan()/billing-and-plans's
     *  matchedPlanCatalogEntry() use — the account's own catalog entry for whatever plan it's
     *  actually subscribed to, needed so /check-out has a plan to prime itself with. */
    private matchedPlanCatalogEntry(): any {
        const setup = this._global.userInfo?.businessSetup?.[0];
        const planCosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
        const planName = String(setup?.subscription?.planName ?? setup?.subscription?.PlanName ?? '').toLowerCase();
        return (
            this._planData.list.find((p: any) => planCosmosId != null && String(p.cosmosId) === String(planCosmosId)) ||
            (planName ? this._planData.list.find((p: any) => String(p.planName).toLowerCase() === planName) : null) ||
            null
        );
    }

    // WARNING — NOT FIXED, RE-ENABLED FOR SANDBOX DEMO ONLY: proceedToCheckout()'s Stripe session
    // (via PaymentPageComponent.buildHardwareOnlyStripePayload()) is still coming back as a
    // RECURRING subscription instead of a one-time hardware charge — confirmed in sandbox testing
    // after two different attempted payload fixes, and there's no known-working one-time-charge
    // contract anywhere in this codebase to copy from. Re-enabled here only because the
    // environment this is pointed at is Stripe's SANDBOX (no real charges possible) for a client
    // demo — MUST be re-disabled (flip back to false, matching billing-and-plans.component.ts's
    // USAGE_HARDWARE_PURCHASE_ENABLED) or fixed for real, with backend confirmation of the
    // one-time-charge contract, before this ever points at production Stripe.
    readonly USAGE_HARDWARE_PURCHASE_ENABLED = true;

    get canProceed(): boolean {
        return this.USAGE_HARDWARE_PURCHASE_ENABLED && this.selectedCount > 0;
    }

    proceedToCheckout(): void {
        if (!this.canProceed) return;

        const plan = this.matchedPlanCatalogEntry();
        if (!plan) {
            this._router.navigate(['/change-plan']);
            return;
        }

        this._signup.selectedPlanDetails = plan;
        localStorage.setItem('selectedPlanDetails', JSON.stringify(plan));
        sessionStorage.setItem(
            'checkout-usage-intent',
            JSON.stringify({
                allowHardware: true,
                hardwareOnly: true,
                presetHardware: this.selectedItems.map((h) => ({ id: h.id, quantity: h.quantity })),
            })
        );
        // Bare /check-out, not /app/check-out — matches billing-and-plans.component.ts's
        // goToUsageCheckout() so this lands on the same standalone, sidebar-free checkout shell
        // every other trip to this page uses, instead of rendering inside the dashboard chrome.
        this._router.navigate(['/check-out']);
    }
}
